import { useState } from "react";
import { SearchProgress } from "@/components/SearchProgress";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/toaster";
import { AMAZON_ASSOCIATES_DISCLOSURE } from "@/lib/affiliate-disclosure";
import { type ProgressStepView, readStreamedOutcome } from "@/lib/progress-stream";

interface SearchQuestion {
  slotId: string;
  label: string;
  question: string;
  required: boolean;
}

interface SearchQuestions {
  categoryId: string;
  categoryLabel: string;
  questions: SearchQuestion[];
  groundingMode?: "category" | "title-only";
  generated?: boolean;
}

interface Category {
  id: string;
  label: string;
  description: string;
}

/**
 * Mirrors `SearchCandidateView` in functions/lib/search.ts. Every string here
 * is produced server-side by the same `present.ts` helpers the CLI uses — the
 * hedged signal wording is a compliance contract, so this component renders
 * those strings and never composes its own.
 */
interface Candidate {
  asin: string;
  name: string;
  snapshot: string;
  status: "shortlisted" | "set-aside" | "filtered-out" | "unverified";
  evidence: string[];
  filterMisses: string[];
  url: string;
}

interface SearchResult {
  categoryLabel: string;
  need: string;
  explanation: string[];
  totalResults: number;
  candidates: Candidate[];
  adjustments: string[];
  unmetRequirements: Array<{ slotId: string; label: string; asked: string }>;
  affiliateDisclosure: boolean;
  groundingMode?: "category" | "title-only";
  generated?: boolean;
  session?: {
    id: string;
    quotedFee: string;
    feeWaived: boolean;
    expiresAt: string;
  };
}

type Stage = "need" | "questions" | "results";

/**
 * The token bucket's own `retryAfterMs`, as an interval a shopper can read.
 *
 * `SearchProgress`'s in-flight formatter renders seconds, which is right for
 * the queued-behind-the-budget line it serves ("it says 12s until our turn") —
 * that wait is capped at `maxAcquireWaitMs`, two minutes. A terminal capacity
 * rejection is the opposite case: the deficit that produced it was too large to
 * sit through, so the realistic figure is ~330,000 ms, and "330s" is a number
 * the reader has to convert. Rounded up at both scales, because rounding down
 * would name an instant the budget has not refilled by.
 *
 * Derived, never invented: the only input is the number the bucket measured
 * and the server returned. Nothing on this page counts it down.
 */
function formatWait(ms: number): string {
  const seconds = Math.max(1, Math.ceil(ms / 1000));
  if (seconds < 90) return `about ${seconds} ${seconds === 1 ? "second" : "seconds"}`;
  const minutes = Math.ceil(seconds / 60);
  return `about ${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
}

export function SearchPage() {
  const [stage, setStage] = useState<Stage>("need");
  const [need, setNeed] = useState("");
  const [questions, setQuestions] = useState<SearchQuestions | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<SearchResult | null>(null);
  const [categories, setCategories] = useState<Category[] | null>(null);
  // The shared Keepa token bucket's own `retryAfterMs` when it turned this
  // search away, in ms. Null whenever we are not in that state. Never a
  // locally-derived number: the only honest interval is the one the bucket
  // measured and sent (#225).
  const [capacityWaitMs, setCapacityWaitMs] = useState<number | null>(null);
  // Kept separate from `need` so the heading keeps naming what they asked for
  // even as they edit the box.
  const [needAsked, setNeedAsked] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  // What the server has told us it finished, in arrival order. Never a timer:
  // every entry is one completed `await` on the request currently in flight.
  const [progress, setProgress] = useState<ProgressStepView[]>([]);
  const { toast } = useToast();

  // biome-ignore lint/suspicious/noExplicitAny: response shape varies per endpoint and status
  const post = async (url: string, body: unknown): Promise<{ response: Response; data: any }> => {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    // Not every failure arrives as our JSON. Cloudflare replaces some origin
    // 5xx responses with its own text/plain error page, so parsing
    // unconditionally throws a JSON syntax error and buries the real status.
    const data = await response.json().catch(() => ({}));
    return { response, data };
  };

  /**
   * The slow endpoints, read as a live feed.
   *
   * Asks for `text/event-stream` and reports each completed step as it arrives.
   * The returned `status` is the one the non-streaming path would have sent —
   * it rides in the terminal event, because an event stream has to commit
   * `200 OK` before the outcome is known. Everything downstream still branches
   * on it, so the four just-in-time outcomes (#144/#150) stay distinct.
   *
   * A server or edge that answers with plain JSON instead is handled by
   * `readStreamedOutcome` and behaves exactly as this page did before.
   */
  const postStreaming = async (url: string, body: unknown) => {
    setProgress([]);
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify(body),
    });
    return readStreamedOutcome(response, (step) => {
      setProgress((previous) => [...previous, step]);
    });
  };

  const handleNeedSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsBusy(true);
    setCategories(null);
    try {
      const { status, data } = await postStreaming("/api/search/questions", { need });

      // 422 means we don't cover the category yet — offer what we do cover
      // rather than dead-ending.
      if (status === 422) {
        setCategories(data.categories ?? []);
        setNeedAsked(data.need ?? need);
        return;
      }
      if (status < 200 || status >= 300) throw new Error(data.error ?? "Something went wrong");

      setQuestions(data);
      setAnswers({});
      setStage("questions");
    } catch (error) {
      toast({
        title: "Couldn't start that search",
        description: error instanceof Error ? error.message : "Something went wrong",
        variant: "destructive",
      });
    } finally {
      setIsBusy(false);
    }
  };

  const pickCategory = async (categoryId: string) => {
    setIsBusy(true);
    try {
      const { response, data } = await post("/api/search/questions", { need, categoryId });
      if (!response.ok) throw new Error(data.error ?? "Something went wrong");
      setCategories(null);
      setQuestions(data);
      setAnswers({});
      setStage("questions");
    } catch (error) {
      toast({
        title: "Couldn't start that search",
        description: error instanceof Error ? error.message : "Something went wrong",
        variant: "destructive",
      });
    } finally {
      setIsBusy(false);
    }
  };

  const handleAnswersSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!questions) return;
    setIsBusy(true);
    setCapacityWaitMs(null);
    try {
      const { status, data } = await postStreaming("/api/search", {
        need,
        categoryId: questions.categoryId,
        answers,
      });
      // 400 with `missing` names the blank required fields — say which.
      if (status === 400 && Array.isArray(data.missing)) {
        throw new Error(
          `${data.error}: ${data.missing.map((q: { label: string }) => q.label).join(", ")}`,
        );
      }
      // Being turned away by our own data budget is a real answer, not a
      // crash: the search is fine, we are momentarily short of the Amazon data
      // it needs, and the bucket told us when that stops being true. A
      // destructive toast would say "something broke" about a condition that
      // resolves on its own — the same conflation #144 fixed one layer up. The
      // answers stay on screen so re-running it costs one click.
      if (status === 503 && data.reason === "at_capacity") {
        setCapacityWaitMs(typeof data.retryAfterMs === "number" ? data.retryAfterMs : 0);
        return;
      }
      if (status < 200 || status >= 300) throw new Error(data.error ?? "Search failed");
      setResult(data);
      setStage("results");
    } catch (error) {
      toast({
        title: "Search failed",
        description: error instanceof Error ? error.message : "Something went wrong",
        variant: "destructive",
      });
    } finally {
      setIsBusy(false);
    }
  };

  const startOver = () => {
    setStage("need");
    setNeed("");
    setQuestions(null);
    setAnswers({});
    setResult(null);
    setCategories(null);
    setCapacityWaitMs(null);
  };

  // Both waits get the feed, and the `need` one especially: it is the one the
  // operator kept reporting (#145). Submitting an uncovered need there runs the
  // whole just-in-time category build — minutes of work — and until now the
  // only sign of it was a button reading "Thinking…".
  if (isBusy && (stage === "need" || stage === "questions")) {
    return (
      <SearchProgress
        steps={progress}
        heading={stage === "need" ? "Working out what to ask you…" : "Working the pond…"}
      />
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-8 py-8">
      {stage === "need" && (
        <>
          <div className="space-y-2">
            <h1 className="text-3xl font-bold tracking-tight">What are you shopping for?</h1>
            <p className="text-muted-foreground">
              Describe it the way you'd say it out loud. We'll ask the few questions that actually
              change the answer.
            </p>
          </div>

          <form onSubmit={handleNeedSubmit} className="space-y-4">
            <Input
              value={need}
              onChange={(e) => setNeed(e.target.value)}
              placeholder="I need a travel crib we can fly with"
              aria-label="What are you shopping for?"
              required
            />
            <Button type="submit" className="w-full" disabled={isBusy || need.trim() === ""}>
              {isBusy ? "Thinking…" : "Start"}
            </Button>
          </form>

          {/* The dead-end path, rewritten after a real shopper searched for a
              "toddler tower" and was told to "pick one to keep going" from a
              list containing nothing like it. Nudging someone into the wrong
              category to avoid an empty screen is the agreeable answer, not
              the honest one. Name what they asked for, explain why the list
              is short, and let the categories be an offer rather than a
              demand. */}
          {categories && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  We don't cover {needAsked ? `"${needAsked}"` : "that"} yet
                </CardTitle>
                <CardDescription>
                  Pickwell works one category at a time — each gets its own hand-built questions and
                  trust checks, which is why the list is short and why the ones on it are worth
                  using. We've noted that you asked for this; it's how we decide what to build next.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="label text-xs text-muted-foreground">Live today</p>
                {categories.map((category) => (
                  <Button
                    key={category.id}
                    variant="outline"
                    className="h-auto w-full justify-start py-3 text-left"
                    onClick={() => pickCategory(category.id)}
                  >
                    <span className="font-medium">{category.label}</span>
                  </Button>
                ))}
                <p className="text-xs text-muted-foreground">
                  If none of these is what you're after, that's the honest answer — better than
                  pointing you at the wrong shelf.
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {stage === "questions" && questions && (
        <>
          <div className="space-y-2">
            <p className="label text-muted-foreground">{questions.categoryLabel}</p>
            <h1 className="text-3xl font-bold tracking-tight">A few questions</h1>
            <p className="text-muted-foreground">
              Leave anything blank if you don't have a preference — we'll simply not filter on it.
              Fields marked <span className="text-destructive">*</span> are needed to build the
              query.
            </p>
          </div>

          {questions.generated && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">We built this category for you</CardTitle>
                <CardDescription>
                  {questions.groundingMode === "title-only"
                    ? "Nobody had asked for this before, so we worked it out just now — and we couldn't pin it to a single Amazon department, so this searches by product name across the catalog. Every trust check still applies; we just can't promise we saw every one."
                    : "Nobody had asked for this before, so we worked out the right questions just now. The trust checks are the same ones every category gets."}
                </CardDescription>
              </CardHeader>
            </Card>
          )}

          {/* Rendered here, above the form the shopper just submitted, for the
              same reason the coverage-gap card is rendered above the category
              list: this is an answer, and their answers are still filled in
              behind it. Every interval in it is derived from the number the
              bucket returned — nothing here counts down, and nothing here
              projects. */}
          {capacityWaitMs !== null && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">We're short of our Amazon data budget</CardTitle>
                <CardDescription>
                  Every search draws on one shared pool of Amazon data, and right now the pool holds
                  less than this search needs. That's our ceiling, not a problem with what you asked
                  for. It refills at a fixed rate, and it says {formatWait(capacityWaitMs)} until
                  our turn — your answers are still here, so running it again then is one click.
                </CardDescription>
              </CardHeader>
            </Card>
          )}

          <form onSubmit={handleAnswersSubmit} className="space-y-6">
            {questions.questions.map((question) => (
              <div key={question.slotId} className="space-y-2">
                <label htmlFor={question.slotId} className="text-sm font-medium">
                  {question.question}
                  {question.required && <span className="text-destructive"> *</span>}
                </label>
                <Input
                  id={question.slotId}
                  value={answers[question.slotId] ?? ""}
                  onChange={(e) =>
                    setAnswers((prev) => ({ ...prev, [question.slotId]: e.target.value }))
                  }
                  placeholder={question.required ? "" : "No preference"}
                  required={question.required}
                  aria-required={question.required}
                />
              </div>
            ))}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={startOver}>
                Back
              </Button>
              <Button type="submit" className="flex-1" disabled={isBusy}>
                {isBusy ? "Working the pond…" : "Find it"}
              </Button>
            </div>
          </form>
        </>
      )}

      {stage === "results" && result && <ResultsView result={result} onStartOver={startOver} />}
    </div>
  );
}

function ResultsView({ result, onStartOver }: { result: SearchResult; onStartOver: () => void }) {
  // Fire-and-forget: the click must open Amazon regardless. `keepalive` lets
  // the request survive the tab losing focus, and a failed metric must never
  // interrupt the purchase.
  const noteClickthrough = () => {
    if (!result.session) return;
    void fetch("/api/clickthrough", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId: result.session.id }),
      keepalive: true,
    }).catch(() => {});
  };

  const shortlisted = result.candidates.filter((c) => c.status === "shortlisted");
  const unmet = result.unmetRequirements ?? [];
  const setAside = result.candidates.filter((c) => c.status !== "shortlisted");

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <p className="label text-muted-foreground">{result.categoryLabel}</p>
        <h1 className="text-3xl font-bold tracking-tight">
          {shortlisted.length === 0
            ? "Came back empty"
            : unmet.length > 0
              ? "Nothing matched everything you asked for"
              : `The retrieve — ${shortlisted.length} ${shortlisted.length === 1 ? "pick" : "picks"}`}
        </h1>
        {/* The completeness claim, and the only place it is made.
            A verified browse node means we really did work the category. A
            title-only search is a keyword match across the catalog — it finds
            the right products often enough to be worth running, but claiming
            we saw the whole category would be a lie, and that claim is the
            thing the trust layer is selling. */}
        <p className="text-muted-foreground">
          {result.groundingMode === "title-only"
            ? `${result.totalResults.toLocaleString()} matches by product name, screened down. We couldn't pin this to one Amazon department, so this isn't every candidate that exists — just every one we could find by name.`
            : `${result.totalResults.toLocaleString()} catalog matches, screened down.`}
        </p>
      </div>

      {/* Above the picks, not below them.
          A stated requirement we could not honor changes what the results
          mean: the shopper asked for something specific and nothing has it.
          Presenting the list as a clean "retrieve" and footnoting the miss
          underneath would be agreeable rather than honest, and being able to
          say "none of these are good" is the whole reason to exist next to
          Amazon's own assistant (CLAUDE.md). */}
      {unmet.length > 0 && shortlisted.length > 0 && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-lg">
              {unmet.length === 1
                ? `Nothing in this category met "${unmet[0].asked}"`
                : "Some of what you asked for could not be met"}
            </CardTitle>
            <CardDescription>
              {unmet.length === 1
                ? "So the results below ignore that one requirement. If it's the part that actually matters, these aren't your answer — a different category or a wider budget usually is."
                : "The results below ignore these requirements:"}
            </CardDescription>
          </CardHeader>
          {unmet.length > 1 && (
            <CardContent className="space-y-1">
              {unmet.map((requirement) => (
                <p key={requirement.slotId} className="text-sm">
                  {requirement.asked}
                </p>
              ))}
            </CardContent>
          )}
        </Card>
      )}

      {/* Honest beats agreeable: an empty retrieve is a real answer, not a
          failure to paper over. */}
      {shortlisted.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Nothing here we'd stand behind</CardTitle>
            <CardDescription>
              Every candidate either missed your requirements or raised a listing-integrity signal
              worth knowing about. Loosening one constraint is usually the fastest way forward.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {shortlisted.map((candidate) => (
        <Card key={candidate.asin}>
          <CardHeader>
            <CardTitle>{candidate.name}</CardTitle>
            {candidate.snapshot && <CardDescription>{candidate.snapshot}</CardDescription>}
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {unmet.length > 0
                ? `Matched everything except ${unmet.map((r) => r.label.toLowerCase()).join(" and ")}, and came through the decoy check clean.`
                : "Matched every filter below and came through the decoy check clean."}
            </p>
            {/* rel="sponsored" marks the commercial relationship to search
                engines; noopener is required with target=_blank. */}
            <Button asChild>
              <a
                href={candidate.url}
                target="_blank"
                rel="sponsored noopener noreferrer"
                onClick={noteClickthrough}
              >
                View on Amazon
              </a>
            </Button>
          </CardContent>
        </Card>
      ))}

      {/* In plain view, next to the links it describes — not buried in a
          footer. FTC requires disclosure; charging a fee while quietly taking
          a commission would burn the trust the product sells (DESIGN.md 5.2,
          BRAND.md). Tracks whether the links are actually tagged rather than
          asserting a commission we may not earn. */}
      {result.affiliateDisclosure && shortlisted.length > 0 && (
        <div className="space-y-2 rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
          <p>
            Pickwell earns a commission if you buy through these links, at no extra cost to you. It
            never affects which products we recommend or set aside — the filters and signals above
            are the whole reason each one is here.
          </p>
          {/* The sentence the Associates Operating Agreement §5 requires,
              verbatim, ADDED to the plain-English paragraph above rather than
              replacing it: the paragraph is what an actual reader
              understands, and it is the FTC-correct disclosure. Conditional
              here, on the same switch as the paragraph, because both describe
              these links. The unconditional copy lives in the footer and the
              terms, where it describes the relationship instead. */}
          <p className="font-medium text-foreground">{AMAZON_ASSOCIATES_DISCLOSURE}</p>
        </div>
      )}

      {/* DESIGN.md §5.4's willingness-to-pay probe: show the fee we would
          have charged, then waive it. The flinch is the data — and saying it
          plainly is also the honest move while we're free. */}
      {result.session?.feeWaived && shortlisted.length > 0 && (
        <p className="rounded-lg border p-4 text-sm">
          <span className="font-medium">
            This search would normally cost {result.session.quotedFee}.
          </span>{" "}
          <span className="text-muted-foreground">It's on us while Pickwell is free.</span>
        </p>
      )}

      {result.adjustments.length > 0 && unmet.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">We loosened a default to find these</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {result.adjustments.map((adjustment) => (
              <p key={adjustment} className="text-sm text-muted-foreground">
                {adjustment}
              </p>
            ))}
          </CardContent>
        </Card>
      )}

      {setAside.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Why the others are out</h2>
          {setAside.map((candidate) => (
            <Card key={candidate.asin}>
              <CardHeader>
                <CardTitle className="text-base">{candidate.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {candidate.status === "unverified" && (
                  <p className="text-sm text-muted-foreground">
                    No tracked history came back, so the signals can't be evaluated — an unscreened
                    listing doesn't make the retrieve.
                  </p>
                )}
                {candidate.evidence.map((sentence) => (
                  <div key={sentence} className="space-y-1">
                    <p className="text-sm">{sentence}</p>
                    <p className="text-xs text-muted-foreground">
                      A behavioral signal worth knowing about before you commit, not a verdict on
                      the seller.
                    </p>
                  </div>
                ))}
                {candidate.filterMisses.map((miss) => (
                  <p key={miss} className="text-sm text-muted-foreground">
                    {miss}
                  </p>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="space-y-2 rounded-lg border p-4">
        <h2 className="text-sm font-semibold">What we filtered on</h2>
        <ul className="space-y-1">
          {result.explanation.map((line) => (
            <li key={line} className="text-sm text-muted-foreground">
              {line}
            </li>
          ))}
        </ul>
      </div>

      <Button variant="outline" onClick={onStartOver}>
        Start another search
      </Button>
    </div>
  );
}
